import React from 'react';
import PropTypes from 'prop-types';
import {
  CaseStudy,
  Reveal,
  ProjectHeader,
  TechChips,
  SectionHeading,
  Note,
  Caption,
  TechList,
  Gallery,
  TreeGrid,
  TreeCard,
  Ln,
  Dir,
  File,
  Cmt,
  Guide,
  Dup,
  Hl,
  SharedContractDiagram,
} from '@components/casestudy';
import agenda from '@images/krafteo/agenda.png';
import rapport from '@images/krafteo/rapport.png';
import activite from '@images/krafteo/activite.png';
import details from '@images/krafteo/details.png';

const KrafteoPage = ({ location }) => (
  <CaseStudy location={location} title="Krafteo">
    <Reveal>
      <ProjectHeader
        eyebrow="Alternance · Développeur Full-Stack"
        title="Krafteo"
        tagline="Monorepo TypeScript et application mobile"
        meta={[
          'Septembre 2025 → présent',
          'Application de gestion pour les équipes terrain',
        ]}>
        <TechChips
          items={[
            { label: 'TypeScript', hot: true },
            { label: 'Monorepo', hot: true },
            { label: 'pnpm + Turborepo', hot: true },
            { label: 'React Native / Expo' },
            { label: 'Next.js' },
            { label: 'Node.js' },
            { label: 'Zod' },
            { label: 'CI/CD' },
          ]}
        />
      </ProjectHeader>
    </Reveal>

    <Reveal>
      <SectionHeading>Le contexte</SectionHeading>
      <p className="cs-lead">
        Krafteo édite une application de gestion pour les équipes terrain : planning partagé,
        dossiers, rapports de chantier et suivi d’activité. J’y prends en charge le cycle complet en
        autonomie, de la conception au déploiement et à la maintenance, en échange direct avec
        l’équipe produit.
      </p>
    </Reveal>

    <Reveal>
      <SectionHeading>La situation à mon arrivée</SectionHeading>
      <p className="cs-lead">
        Le produit tenait dans <strong>deux dépôts sans lien entre eux</strong> : d’un côté
        l’application web Next.js, de l’autre l’API Express / Prisma. Les schémas de validation
        vivaient dans le serveur, et le web redéclarait à la main ce dont il avait besoin.
      </p>
      <p className="cs-lead">
        <strong>L’application mobile, elle, n’existait pas encore.</strong> Elle allait devenir une
        troisième surface, avec les mêmes formes de données à réécrire une fois de plus.
      </p>

      <TreeGrid>
        <TreeCard name="krafteo-apps" tag={{ label: 'dépôt 1', kind: 'bad' }}>
          <Ln>
            <Cmt># le web (Next.js)</Cmt>
          </Ln>
          <Ln>
            <Guide>├── </Guide>
            <Dir>app/</Dir>
          </Ln>
          <Ln>
            <Guide>├── </Guide>
            <Dir>components/</Dir>
          </Ln>
          <Ln>
            <Guide>├── </Guide>
            <Dir>lib/</Dir>
          </Ln>
          <Ln>
            <Guide>└── </Guide>
            <File>package.json</File>
          </Ln>
        </TreeCard>

        <TreeCard name="addons-api" tag={{ label: 'dépôt 2', kind: 'bad' }}>
          <Ln>
            <Cmt># l’API (Express)</Cmt>
          </Ln>
          <Ln>
            <Guide>├── </Guide>
            <Dir>src/</Dir>
          </Ln>
          <Ln>
            <Guide>│   ├── </Guide>
            <Dir>controllers/</Dir>
          </Ln>
          <Ln>
            <Guide>│   └── </Guide>
            <Dup>validators/</Dup>
          </Ln>
          <Ln>
            <Cmt>{'        les schémas ici'}</Cmt>
          </Ln>
        </TreeCard>

        <TreeCard name="mobile" tag={{ label: 'n’existe pas', kind: 'bad' }}>
          <Ln>
            <Cmt># à créer</Cmt>
          </Ln>
          <Ln>{' '}</Ln>
          <Ln>
            <Cmt>{'  une 3ᵉ surface, et'}</Cmt>
          </Ln>
          <Ln>
            <Cmt>{'  les mêmes schémas'}</Cmt>
          </Ln>
          <Ln>
            <Cmt>{'  à réécrire encore'}</Cmt>
          </Ln>
        </TreeCard>
      </TreeGrid>
      <Caption>
        <b>Avant.</b> Deux dépôts séparés, aucun espace commun : ni configuration TypeScript
        partagée, ni schémas partagés, ni utilitaires. Chaque surface repartait de zéro.
      </Caption>

      <Note>
        <b>Le vrai coût.</b> Ce n’est pas le temps de recopier un schéma, c’est qu’une évolution de
        l’API ne cassait rien à la compilation. Le client gardait son ancienne définition, compilait
        sans la moindre erreur, et le bug n’apparaissait qu’à l’exécution, souvent après la mise en
        production.
      </Note>
    </Reveal>

    <Reveal>
      <SectionHeading>Ce que j’ai mis en place</SectionHeading>
      <p className="cs-lead">
        J’ai proposé de réunir les applications dans un <strong>monorepo TypeScript</strong>, sur
        pnpm workspaces et Turborepo. J’ai initialisé la structure, déplacé l’application web dans{' '}
        <code>apps/web</code>, puis <strong>créé l’application mobile de zéro</strong> dans{' '}
        <code>apps/mobile</code> (React Native, Expo). J’ai ensuite centralisé l’outillage commun :
        configuration TypeScript, ESLint, Prettier et hooks de pre-commit.
      </p>

      <TreeCard name="krafteo-apps/" tag={{ label: 'monorepo', kind: 'good' }} style={{ marginTop: '16px' }}>
        <Ln>
          <Guide>├── </Guide>
          <Dir>apps/</Dir>
        </Ln>
        <Ln>
          <Guide>│   ├── </Guide>
          <Dir>web/</Dir>
          <Cmt>{'          # Next.js, déplacé depuis la racine'}</Cmt>
        </Ln>
        <Ln>
          <Guide>│   ├── </Guide>
          <Dir>mobile/</Dir>
          <Cmt>{'       # React Native + Expo, créée de zéro'}</Cmt>
        </Ln>
        <Ln>
          <Guide>│   ├── </Guide>
          <Dir>server/</Dir>
          <Cmt>{'       # Express + Prisma, rapatrié'}</Cmt>
        </Ln>
        <Ln>
          <Guide>│   └── </Guide>
          <Dir>admin/</Dir>
          <Cmt>{'        # back-office'}</Cmt>
        </Ln>
        <Ln>
          <Guide>│</Guide>
        </Ln>
        <Ln on>
          <Guide>├── </Guide>
          <Hl>packages/</Hl>
        </Ln>
        <Ln on>
          <Guide>│   ├── </Guide>
          <Hl>contracts/</Hl>
          <Cmt>{'    # @krafteo/contracts : schémas et types partagés'}</Cmt>
        </Ln>
        <Ln on>
          <Guide>│   │   └── </Guide>
          <Dir>src/</Dir>
          <Cmt>{'     # validators · types · lib'}</Cmt>
        </Ln>
        <Ln>
          <Guide>│   ├── </Guide>
          <Dir>permissions/</Dir>
          <Cmt>{'  # droits d’accès partagés'}</Cmt>
        </Ln>
        <Ln>
          <Guide>│   ├── </Guide>
          <Dir>address-utils/</Dir>
        </Ln>
        <Ln>
          <Guide>│   ├── </Guide>
          <Dir>rrule-utils/</Dir>
          <Cmt>{'   # récurrences d’agenda'}</Cmt>
        </Ln>
        <Ln>
          <Guide>│   └── </Guide>
          <Dir>typescript-config/</Dir>
        </Ln>
        <Ln>
          <Guide>│</Guide>
        </Ln>
        <Ln>
          <Guide>├── </Guide>
          <File>pnpm-workspace.yaml</File>
          <Cmt>{'      # mon commit d’initialisation'}</Cmt>
        </Ln>
        <Ln>
          <Guide>└── </Guide>
          <File>turbo.json</File>
        </Ln>
      </TreeCard>
      <Caption>
        <b>Après.</b> Une seule arborescence. Les applications vivent dans <code>apps/</code> et
        partagent depuis <code>packages/</code> tout ce qui doit l’être.
      </Caption>
    </Reveal>

    <Reveal>
      <SectionHeading>Ce que j’ai fait, et ce que l’équipe a fait</SectionHeading>
      <p className="cs-lead">
        Autant être précis, parce que c’est un travail d’équipe.{' '}
        <strong>Le monorepo, l’application mobile et la configuration partagée sont mon travail.</strong>{' '}
        Sur le package <code>contracts</code>, j’ai assisté mon maître d’apprentissage pour sa
        création et la migration des schémas du serveur.
      </p>
      <p className="cs-lead">
        Mais c’est le monorepo qui l’a rendu possible : avant, il n’y avait aucun endroit où le
        mettre. Et ma contribution principale aux contrats est du côté client, c’est l’objet de la
        section suivante.
      </p>
    </Reveal>

    <Reveal>
      <SectionHeading>Le reste de mon périmètre</SectionHeading>
      <ul className="fancy-list">
        <li>
          <strong>L’application mobile, de zéro jusqu’aux stores.</strong> Expo Router, design system
          NativeWind, écrans d’authentification, gestion d’erreurs, puis publication. C’est
          aujourd’hui l’essentiel de mon travail chez Krafteo.
        </li>
        <li>
          <strong>Des packages partagés.</strong> J’ai développé les utilitaires communs de
          récurrence d’agenda, d’adresses et de permissions, consommés par plusieurs applications du
          monorepo.
        </li>
        <li>
          <strong>La CI/CD et les livraisons mobiles.</strong> Builds et soumissions via EAS, mises à
          jour OTA pour corriger sans repasser par la revue des stores, et campagnes de bêta-test
          externes. Un cycle court entre déploiement, retour et itération.
        </li>
        <li>Les relectures de code et le process Git de l’équipe.</li>
      </ul>
    </Reveal>

    <Reveal>
      <SectionHeading>Ce que ça change</SectionHeading>
      <p className="cs-lead">
        Une application échange en permanence des données avec son serveur : un rendez-vous, un
        client, un rapport d’intervention. Pour se comprendre, les deux doivent être d’accord sur la
        forme exacte de ces données.
      </p>
      <p className="cs-lead">
        Avant, chaque application décrivait cette forme dans son coin. Quand quelqu’un en modifiait
        une et oubliait les autres, rien ne signalait l’erreur. Elle n’apparaissait que plus tard,
        chez l’utilisateur. Aujourd’hui, cette description est écrite{' '}
        <strong>une seule fois, à un seul endroit</strong>, et toutes les applications s’y réfèrent.
      </p>

      <SharedContractDiagram />

      <Note>
        <b>L’erreur est attrapée par la machine, plus par l’utilisateur.</b> Si quelqu’un modifie la
        description partagée, les applications refusent de se construire tant qu’elles ne sont pas à
        jour. Le problème est bloqué sur le poste du développeur, avant même d’arriver en production.
      </Note>

      <p className="cs-lead">
        Ma contribution : j’ai branché l’application mobile sur cette description partagée. Elle ne
        redécrit plus les données, elle les reprend. Et c’est le monorepo que j’ai mis en place qui a
        rendu ce partage possible, puisqu’avant il n’existait aucun endroit où poser quoi que ce soit
        de commun.
      </p>
    </Reveal>

    <Reveal>
      <SectionHeading>L’application mobile</SectionHeading>
      <p className="cs-lead">
        Créée de zéro et publiée sur l’App Store et Google Play, elle est aujourd’hui l’essentiel de
        mon travail chez Krafteo.
      </p>
      <Gallery
        columns={4}
        columnsSm={2}
        items={[
          {
            src: agenda,
            alt: 'Krafteo, agenda partagé',
            caption: (
              <>
                <b>Agenda partagé.</b> Le planning de l’utilisateur et celui de ses équipes.
              </>
            ),
          },
          {
            src: rapport,
            alt: 'Krafteo, rapport de chantier',
            caption: (
              <>
                <b>Rapports de chantier.</b> Fiches d’auto-contrôle remplies sur le terrain.
              </>
            ),
          },
          {
            src: activite,
            alt: 'Krafteo, suivi d’activité d’un dossier',
            caption: (
              <>
                <b>Suivi d’activité.</b> Fil du dossier, commentaires et prochaine action.
              </>
            ),
          },
          {
            src: details,
            alt: 'Krafteo, détail d’un événement',
            caption: (
              <>
                <b>Détail d’un chantier.</b> Durée, statut, adresse, contact et dossier.
              </>
            ),
          },
        ]}
      />
    </Reveal>

    <Reveal>
      <SectionHeading>Technologies</SectionHeading>
      <TechList
        items={[
          'TypeScript',
          'pnpm workspaces',
          'Turborepo',
          'React Native',
          'Expo',
          'Next.js',
          'Node.js',
          'Express',
          'Prisma',
          'Zod',
          'NativeWind',
          'CI/CD',
        ]}
      />
    </Reveal>
  </CaseStudy>
);

KrafteoPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default KrafteoPage;
